import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { getAllEvents, getAllOrgs } from "../../../api/AffiliateApi";
import { getMajorClubs } from "../../../api/ClubApi";
import { updatePersonnelStatus } from "../../../api/PersonnelApi";
import { PersonnelTypes } from "../../../constants/PersonnelTypes";
import { useAffiliation } from "../../../contexts/AffiliationContext";
import { useAlert } from "../../../contexts/AlertsContext";
import { BigsClub } from "../../../types/personnel";
import { BigsSelect } from "../BigsBcrWrapper";
import { RequiredLabel } from "../FormElements";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";

type ReactivatePersonnelBodyProps = {
    personnel: any;
    callback: Function;
    selectedOrg: any;
    setSelectedOrg: Function;
    selectedAffiliation: any;
    setSelectedAffiliation: Function;
};

const BodyContent = styled.div.attrs(() => ({
    className: "mt-4 ml-4",
}))`
    text-align: left;
    margin-bottom: 200px;
`;

const TopTextStyle = styled.div.attrs(() => ({}))`
    &&& {
        font-size: 16px;
        color: var(--slate);
        line-height: 24px;
        text-style: Helvetica;
    }
`;

const BottomTextStyle = styled.div.attrs(() => ({}))`
    &&& {
        font-size: 12px;
        text-style: Helvetica;
        font-weight: 300;
        width: 400px;
    }
`;

const mapAffiliationOrgsToOptions = (response: any[]) => {
    const safeList = (Array.isArray(response) ? response : []).filter(
        (org: any) => org?.orgId != null && org?.name,
    );
    return safeList
        .map((org: any) => ({
            value: org.id,
            label: org.name,
            teamId: org.teamId,
            orgId: org.orgId,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
};

const ReactivatePersonnelBody: React.FC<ReactivatePersonnelBodyProps> = ({
    personnel,
    selectedOrg,
    setSelectedOrg,
    setSelectedAffiliation,
    selectedAffiliation,
}) => {
    const {
        affiliationDropdownOptions,
        refreshAffiliationDropdownByOrgId,
        refreshClubDropdownByOrgStatsId,
    } = useAffiliation();
    const [orgDropdownOptions, setOrgDropdownOptions] = useState<any[]>([]);

    const personnelType = personnel?.personnelType?.type;
    const isClubPersonnel = personnelType === PersonnelTypes.CLUB;
    const isUmpirePersonnel = personnelType === PersonnelTypes.UMPIRE;

    useEffect(() => {
        if (isClubPersonnel) {
            getMajorClubs().then((response: BigsClub[]) => {
                const safeList = (Array.isArray(response) ? response : []).filter(
                    (club: BigsClub) => club?.orgStatsId != null && club?.name,
                );
                const orgOptions = safeList
                    .map((club: BigsClub) => ({
                        value: club.clubStatsId,
                        label: club.name,
                        teamId: club.clubStatsId,
                        orgId: club.orgStatsId,
                        clubStatsId: club.clubStatsId,
                    }))
                    .sort((a: any, b: any) => a.label.localeCompare(b.label));
                setOrgDropdownOptions(orgOptions);
            });
            return;
        }

        if (isUmpirePersonnel) {
            getAllOrgs("Umpire").then(response => {
                setOrgDropdownOptions(mapAffiliationOrgsToOptions(response));
            });
            return;
        }

        // VMS / volunteer: events only (exclude Club majors that leak via getAllOrgs("Club"))
        getAllEvents().then(response => {
            setOrgDropdownOptions(mapAffiliationOrgsToOptions(response));
        });
    }, [personnelType, isClubPersonnel, isUmpirePersonnel]);

    const orgChangeHandler = (orgOption: any) => {
        setSelectedOrg(orgOption);
        setSelectedAffiliation(null); // Clear team selection when org changes
        if (isClubPersonnel) {
            refreshClubDropdownByOrgStatsId(orgOption.orgId);
        } else {
            refreshAffiliationDropdownByOrgId(orgOption.orgId);
        }
    };

    const affiliationChangeHandler = (affiliationOption: any) => {
        setSelectedAffiliation(affiliationOption);
    };

    return (
        <BodyContent>
            <TopTextStyle>
                Add {personnel.lastName}, {personnel.firstName} to the following Roster.
            </TopTextStyle>
            <div className="mr-1 mt-2">
                <RequiredLabel text="Organization" required />
                <BottomTextStyle>
                    <BigsSelect
                        value={selectedOrg}
                        isDisabled={false}
                        onChange={(orgOpt: any) => {
                            orgChangeHandler(orgOpt);
                        }}
                        options={orgDropdownOptions}
                        placeholder="select..."
                    />
                </BottomTextStyle>
            </div>
            <div className="mr-1 mt-2">
                <RequiredLabel text="Team" required />
                <BottomTextStyle>
                    <BigsSelect
                        value={selectedAffiliation}
                        isDisabled={!selectedOrg}
                        onChange={(affiliationOpt: any) => {
                            affiliationChangeHandler(affiliationOpt);
                        }}
                        options={affiliationDropdownOptions}
                        placeholder="select..."
                    />
                </BottomTextStyle>
            </div>
        </BodyContent>
    );
};

type ReactivatePersonnelFooterProps = {
    personnel: {
        id: number;
        status: string;
        affiliation: any;
    };
    closeModal: Function;
    callback: Function;
    selectedOrg: any;
    selectedAffiliation: any;
};

const ReactivatePersonnelFooter: React.FC<ReactivatePersonnelFooterProps> = ({
    personnel,
    closeModal,
    callback,
    selectedOrg,
    selectedAffiliation,
}) => {
    const showAlert = useAlert();
    const addPersonnelHandler = () => {
        // For CLUB, path param is clubStatsId; for VMS/Umpire it remains affiliation teamId.
        const assignmentId = selectedAffiliation.clubStatsId ?? selectedAffiliation.teamId;
        updatePersonnelStatus(personnel.id, "ACTIVE", assignmentId)
            .then(() => {
                if (callback && typeof callback === "function") {
                    callback(selectedAffiliation);
                }
                showAlert("Added to Roster");
            })
            .catch(() => {
                showAlert("Error adding personnel to roster", "danger");
            });
    };

    return (
        <div className="d-flex">
            <PrimaryButtonStyled
                height={26}
                width={180}
                disabled={!selectedOrg || !selectedAffiliation}
                onClick={event => {
                    event.stopPropagation();
                    closeModal();
                    addPersonnelHandler();
                }}
            >
                Add to Roster
            </PrimaryButtonStyled>
            <div className="pl-2">
                <OutlinedButtonStyled
                    width={80}
                    height={26}
                    disabled={false}
                    onClick={event => {
                        event.stopPropagation();
                        closeModal();
                    }}
                >
                    Cancel
                </OutlinedButtonStyled>
            </div>
        </div>
    );
};

ReactivatePersonnelBody.displayName = "BodyContent";
ReactivatePersonnelFooter.displayName = "FooterContent";

export { ReactivatePersonnelBody, ReactivatePersonnelFooter };
